/// GA4 / Firebase Analytics wrapper for the web port.
///
/// Mirrors `FlatoutATC/Support/AnalyticsManager.swift` so events
/// from iOS and web aggregate cleanly in the same GA4 property
/// (G-T2V1GBLVCL). Same event names, same parameter keys where
/// the concept translates. Web-only additions (signin / signup /
/// redeem_code) are added at the bottom; iOS-only events
/// (tracking_authorization, AdMob ad_*, ground_level_start) are
/// not surfaced here.
///
/// All methods are fire-and-forget and never throw. If the
/// analytics SDK isn't initialized (dev build, opted-out user,
/// pre-init startup race), calls are silently no-op'd so call
/// sites don't have to check anything.

import { logEvent, setUserId } from 'firebase/analytics'
import { awaitAnalytics, isAnalyticsAvailable } from '../firebase/app'

interface NumericMap {
  [key: string]: string | number | boolean | undefined
}

/// Internal helper. Catches any SDK error so callers don't have
/// to wrap each event in try/catch — analytics failures should
/// never bubble up into gameplay.
///
/// Awaits the analytics SDK init so the first event of a session
/// (typically the `purchase` event on the Stripe return page)
/// doesn't get dropped — the sync `analytics()` getter returns
/// null until init resolves, which previously caused silent loss.
async function safeLog(event: string, params?: NumericMap): Promise<void> {
  if (!isAnalyticsAvailable()) return
  try {
    const a = await awaitAnalytics()
    if (!a) return
    logEvent(a, event, params as Record<string, unknown> | undefined)
  } catch (err) {
    console.warn(`Analytics ${event} failed`, err)
  }
}

/// Bind the Firebase UID so cross-platform user analytics can
/// stitch together. Call after sign-in or anonymous bootstrap.
export async function setAnalyticsUserId(uid: string | null): Promise<void> {
  if (!isAnalyticsAvailable()) return
  try {
    const a = await awaitAnalytics()
    if (!a) return
    setUserId(a, uid)
  } catch (err) {
    console.warn('Analytics setUserId failed', err)
  }
}

// ─── Page views ───────────────────────────────────────────────

/// GA4 reserved `page_view` event. Fired on every screen change (see
/// App.tsx's effect on `[screen.kind]`) now that a single page load can
/// move between several logical "pages" (home, game, dev blog, tower log,
/// dashboard) without a real navigation — GA's own SPA auto-tracking only
/// sees the first load, so this fills the gap explicitly.
export function logPageView(pagePath: string): void {
  safeLog('page_view', {
    page_path: pagePath,
    page_location: window.location.href,
  })
}

// ─── Level lifecycle ──────────────────────────────────────────

export function logLevelStart(args: {
  levelId: string
  difficulty: string
  isFree: boolean
}): void {
  safeLog('level_start', {
    level_id: args.levelId,
    game_mode: 'air', // matches iOS GameMode enum string for the air radar
    difficulty: args.difficulty,
    is_free: args.isFree,
  })
}

export function logLevelEnd(args: {
  levelId: string
  difficulty: string
  success: boolean
  finalScore: number
  successCount: number
  totalHandled: number
}): void {
  safeLog('level_end', {
    level_id: args.levelId,
    difficulty: args.difficulty,
    success: args.success ? 'true' : 'false', // string to match iOS shape
    final_score: args.finalScore,
    success_count: args.successCount,
    total_handled: args.totalHandled,
  })
}

// ─── Onboarding / tutorial ────────────────────────────────────

export function logOnboardingCompleted(): void {
  safeLog('onboarding_complete')
}

export function logTutorialStarted(): void {
  safeLog('tutorial_start')
}

export function logTutorialCompleted(): void {
  safeLog('tutorial_complete')
}

// ─── What's New modal ────────────────────────────────────────

/// Fires whenever the What's New modal becomes visible. `trigger`
/// distinguishes the auto-open path (a new releaseId was detected
/// since this device's last dismissal) from the manual button in the
/// HomeScreen header. Useful for measuring read-rate vs deliberate
/// open rate. `release_id` is the WHATS_NEW[0].releaseId at view time.
export function logWhatsNewViewed(args: {
  releaseId: string
  trigger: 'auto' | 'manual'
}): void {
  safeLog('view_whats_new', {
    release_id: args.releaseId,
    trigger: args.trigger,
  })
}

// ─── Purchase funnel ──────────────────────────────────────────

export function logPurchaseScreenViewed(args: {
  levelId: string
}): void {
  safeLog('view_purchase_screen', {
    level_id: args.levelId,
  })
}

export function logPurchaseButtonTapped(args: {
  levelId: string
  productId: string
}): void {
  safeLog('tap_purchase_button', {
    level_id: args.levelId,
    item_id: args.productId,
  })
}

/// Firebase reserved `purchase` event — drives revenue tracking
/// in GA4. Mirrors iOS `logInAppPurchase`. Fire after the
/// post-checkout return confirms success.
export function logPurchaseCompleted(args: {
  productId: string
  productName: string
  priceUSD: number
  transactionId: string
}): void {
  safeLog('purchase', {
    item_id: args.productId,
    item_name: args.productName,
    price: args.priceUSD,
    currency: 'USD',
    transaction_id: args.transactionId,
    value: args.priceUSD,
  })
}

// ─── Tower Log ────────────────────────────────────────────────
//
// The web uid is already attached to every event via `setAnalyticsUserId`
// (called once from App.tsx after profile boot) — no extra user-property
// work needed here, "the board already does" this for every event, Tower
// Log included.

/// Fires once per page visit.
export function logTowerLogViewed(): void {
  safeLog('towerlog_view')
}

/// Fires after a vote on a Tower Log board entry (post or comment) actually
/// lands server-side. Distinct from the plain board's votes (which aren't
/// instrumented at all today) so Tower Log engagement is visible on its own.
export function logTowerLogVote(): void {
  safeLog('towerlog_vote')
}

/// Fires after a reply is posted on a Tower Log board entry.
export function logTowerLogComment(): void {
  safeLog('towerlog_comment')
}

// ─── Auth ─────────────────────────────────────────────────────

/// Sign-in. `provider` matches the AuthProvider type:
/// 'google' | 'apple' | 'email' | 'anonymous'.
export function logSignIn(provider: string): void {
  safeLog('login', { method: provider }) // GA4 reserved `login` event
}

/// Sign-up (account creation). Same providers as logSignIn.
export function logSignUp(provider: string): void {
  safeLog('sign_up', { method: provider }) // GA4 reserved `sign_up` event
}

// ─── iOS → web redemption codes ───────────────────────────────

export function logRedeemCode(args: {
  unlockType: string
}): void {
  safeLog('redeem_code', {
    unlock_type: args.unlockType,
  })
}

// ─── Errors ───────────────────────────────────────────────────

/// Mirrors iOS `logException`. Web has no Crashlytics
/// equivalent here, so this is the only logging path —
/// browser-uncaught errors should also call this from the
/// global handler if we wire one up later.
export function logException(args: {
  context: string
  error: unknown
  isFatal?: boolean
}): void {
  const err = args.error
  const errorMessage = err instanceof Error ? err.message : String(err)
  const errorName = err instanceof Error ? err.name : 'Unknown'
  safeLog('app_exception', {
    context: args.context,
    error_name: errorName,
    error_message: errorMessage,
    is_fatal: args.isFatal ?? false,
  })
}
